import { CancelToken } from 'axios';
import client from '@/utils/request';
import { getApiUrlWithParams } from '@/utils/url';
import {
  LOG_API_BASE_URL,
  TANZHI_API_BASE_URL as API_BASE_URL,
} from './base_url';
import { PRODUCTION_URL } from '@/constants/router-path';
import { ResumeBuilderType } from '@/types/resume';

/**
 * HomePage apis
 */

export const searchByQuery = (
  query: string,
  type = 'name_search',
  need_alias = true,
) => {
  const requestUrl = getApiUrlWithParams(`${API_BASE_URL}/search_by_query/`, {
    query,
    type,
    need_alias,
  });
  return client.get(requestUrl);
};

/**
 * Job titles apis
 */

export const getJobTitleData = (id: string, city?: string) => {
  const url = getApiUrlWithParams(`${API_BASE_URL}/job_titles/${id}/`, {
    city,
  });
  return client.get(url);
};

export const getJobTitleCity = (jobTitleId: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/job_titles/province_city_data_by_id`,
    {
      job_title_id: jobTitleId,
    },
  );
  return client.get(requestUrl);
};

export const getHotJobOpportunities = (query: {
  jobTitle: string;
  city?: string;
  size?: number;
  offset?: number;
}) => {
  const url = getApiUrlWithParams(
    `${API_BASE_URL}/job_titles/${query.jobTitle}/hot_job_opportunities/`,
    query,
  );
  return client.get(url);
};

/**
 * Subscribe apis
 */
export const subscribeJob = (
  email: string,
  job: { id: string; name: string },
  location: { id: string; name: string },
) => {
  return client.post(`${API_BASE_URL}/job_subscriptions/`, {
    email,
    job,
    location,
  });
};

export const cancelSubscribeJob = (cancelId: string) => {
  return client.get(
    `${API_BASE_URL}/job_subscriptions/${cancelId}/cancel_job_subscription/`,
  );
};

/**
 * Job ranking apis
 */

export const getJobTitleRankData = (
  id?: string,
  city?: string,
  page?: number,
) => {
  const url = getApiUrlWithParams(
    `${API_BASE_URL}/job_titles/job_title_rank/`,
    {
      job_title_id: id,
      city,
      page,
    },
  );
  return client.get(url);
};

/**
 * Job board apis
 */
export const submitResume = (job_id: string, resume_id: string) => {
  return client.post(`${API_BASE_URL}/job_opportunities/submit_resume/`, {
    job_id,
    resume_id,
  });
};

/**
 * JobSeeker profile apis
 */
export const updateJobSeekerProfile = (data: any) => {
  return client.patch(`${API_BASE_URL}/jobseeker_profiles/my_profile/`, data);
};

/**
 * JobSeeker resumes apis
 */
export const getJobSeekerResumes = () => {
  return client.get(`${API_BASE_URL}/jobseeker_resumes/get_resumes/`);
};

export const getJobSeekerResume = (id: string) => {
  return client.get(`${API_BASE_URL}/jobseeker_resumes/${id}/my_resume/`);
};

export const createJobSeekerResume = () => {
  return client.post(`${API_BASE_URL}/jobseeker_resumes/create_resume/`);
};

export const updateJobSeekerResume = (
  data: Partial<ResumeBuilderType>,
  id: string,
) => {
  return client.patch(
    `${API_BASE_URL}/jobseeker_resumes/${id}/my_resume/`,
    data,
  );
};

export const copyJobSeekerResume = (id: string) => {
  return client.post(`${API_BASE_URL}/jobseeker_resumes/${id}/copy_resume/`);
};

export const deleteJobSeekerResume = (id: string) => {
  return client.delete(`${API_BASE_URL}/jobseeker_resumes/${id}/my_resume/`);
};

export const updateJobSeekerResumeAvatar = (data: Blob, id: string) => {
  const formData = new FormData();
  formData.append('avatar', data);

  return client.post(
    `${API_BASE_URL}/jobseeker_resumes/${id}/resume_avatar/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

export const uploadJobSeekerResume = (file: File | null) => {
  if (!file) {
    return;
  }
  const formData = new FormData();
  formData.append('resume_file', file);
  return client.post(
    `${API_BASE_URL}/jobseeker_resumes/upload_resume/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

export const getUploadResumeStatus = (task_id: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/jobseeker_resumes/upload_resume_status/`,
    {
      id: task_id,
    },
  );
  return client.get(requestUrl);
};

/**
 * JobSeeker evaluation resume apis
 */
export const uploadEvaluationResume = (
  expect_job_title: { id: string; name: string },
  file?: File | null,
) => {
  const formData = new FormData();
  file && formData.append('resume_file', file);
  expect_job_title &&
    formData.append('expect_job_title', JSON.stringify(expect_job_title));
  return client.post(
    `${API_BASE_URL}/evaluation_resume/upload_resume/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

export const evaluateResume = (
  resume_id: string,
  expect_job_title: { id: string; name: string } | null,
) => {
  const formData = new FormData();
  expect_job_title &&
    formData.append('expect_job_title', JSON.stringify(expect_job_title));
  return client.post(
    `${API_BASE_URL}/evaluation_resume/${resume_id}/evaluate_resume/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};

export const getEvaluationProgress = (id: string) => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/${id}/get_evaluation_progress/`,
  );
};

export const saveEvaluationResume = (id: string) => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/${id}/save_evaluation_resume/`,
  );
};

export const getEvaluationResume = (id: string) => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/${id}/get_evaluation_resume/`,
  );
};

export const getEvaluationResumes = () => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/get_evaluation_resumes/`,
  );
};

export const deleteEvaluationResume = (id: string) => {
  return client.delete(
    `${API_BASE_URL}/evaluation_resume/${id}/delete_evaluation_resume/`,
  );
};

export const saveEvaluationResumeTitle = (
  data: { title: string },
  id: string,
) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/evaluation_resume/${id}/evaluation_title/`,
    { title: data.title },
  );
  return client.patch(requestUrl);
};

export const createEvaluationPaymentOrder = (id: string) => {
  return client.get(`${API_BASE_URL}/evaluation_resume/${id}/payment_order/`);
};

export const downloadEvaluationReport = (id: string) => {
  return client.get(
    `${API_BASE_URL}/evaluation_resume/${id}/download_report/`,
    {},
    { responseType: 'blob' },
  );
};

export const getPreviewResume = (
  resume_id: string,
  template_id?: string,
  cancelToken?: CancelToken,
) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/jobseeker_resumes/${resume_id}/preview_resume/`,
    { template_id },
  );
  return client.get(requestUrl, {}, { cancelToken, responseType: 'blob' });
};

export const getResumeTemplates = () => {
  return client.get(`${API_BASE_URL}/resume_templates/get_templates/`);
};

/**
 * Company apis
 */
export const getJobCompanyCity = (companyId: string) => {
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/companies/jobs/province_city_data_by_id`,
    {
      id: companyId,
    },
  );
  return client.get(requestUrl);
};

export const searchCompanyJDJobTitleByQuery = (
  companyId: string,
  query: string | null,
) => {
  return client.get(
    `${API_BASE_URL}/companies/${companyId}/job_title_name_search/`,
    {
      query,
    },
  );
};

export const searchCompanyJob = (
  companyId: string,
  jobTitleId?: string,
  locationId?: string,
  // salaryRange?: { min: number, max: number},
) => {
  const params: Record<string, any> = {};
  if (jobTitleId) {
    params.job_title_id = jobTitleId;
  }
  if (locationId) {
    params.location_id = locationId;
  }
  const requestUrl = getApiUrlWithParams(
    `${API_BASE_URL}/companies/${companyId}/search_job/`,
    params,
  );
  return client.get(requestUrl);
};

/**
 *
 * KGC concept model apis
 */
export const getProvinceCityData = () => {
  return client.get(`${API_BASE_URL}/locations/province_city_data/`);
};

export const getSchoolOptions = (school: string) => {
  const requestUrl = getApiUrlWithParams(`${API_BASE_URL}/schools/search/`, {
    query: school,
  });
  return client.get(requestUrl);
};

export const getMajorOptions = (major: string) => {
  const requestUrl = getApiUrlWithParams(`${API_BASE_URL}/majors/search/`, {
    query: major,
  });
  return client.get(requestUrl);
};

export const getIndustryData = () => {
  return client.get(`${API_BASE_URL}/industries/industry_data/`);
};

/**
 * wordPress apis
 */
export const getArticleTags = () => {
  return client.get(
    `${PRODUCTION_URL}/articles/wp-json/wp/v2/tags?per_page=100&_fields=id,name`,
  );
};

export const getArticlesByTags = (tagIds: string) => {
  return client.get(
    `${PRODUCTION_URL}/articles/wp-json/wp/v2/posts?tags=${tagIds}&per_page=100&_fields=title,id,date,excerpt,link,tags,featured_media`,
  );
};

/**
 * event tracker apis
 */

export const sendEventTracker = (
  data: {
    name: string;
    [key: string]:
      | string
      | number
      | Record<string, string | number | null>[]
      | null
      | undefined;
  },
  options?: Record<string, unknown>,
) => {
  return client.post(`${LOG_API_BASE_URL}/`, data, options);
};

/**
 * error apis
 */
export const sendError = (data: any) => {
  return client.post(`${API_BASE_URL}/error/error_report/`, data);
};
